"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/el";
import "dayjs/locale/es";
import { translateFromCatalogs, type LocaleCatalogs } from "./catalog";
import { ThemeLocaleProvider } from "@tranquility-ai/theme-components";
import { useOptionalEnabledLocales } from "./EnabledLocalesProvider";
import {
  APP_LOCALE_STORAGE_KEY,
  APP_LOCALES,
  DEFAULT_APP_LOCALE,
  LEGACY_HELIOTRACE_LOCALE_STORAGE_KEY,
  LOCALE_OPTIONS,
  isAppLocale,
  localeHtmlLang,
  type AppLocale,
  type AppLocaleOption,
} from "./locales";

function storeAppLocale(locale: AppLocale): void {
  try {
    window.localStorage.setItem(APP_LOCALE_STORAGE_KEY, locale);
  } catch {
    /* ignore quota / private mode */
  }
}

export function readStoredLocale(): AppLocale {
  if (typeof window === "undefined") return DEFAULT_APP_LOCALE;
  try {
    const stored = window.localStorage.getItem(APP_LOCALE_STORAGE_KEY);
    if (isAppLocale(stored)) return stored;

    const legacy = window.localStorage.getItem(LEGACY_HELIOTRACE_LOCALE_STORAGE_KEY);
    if (isAppLocale(legacy)) {
      storeAppLocale(legacy);
      return legacy;
    }
    return DEFAULT_APP_LOCALE;
  } catch {
    return DEFAULT_APP_LOCALE;
  }
}

type I18nValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  enabledLocaleOptions: readonly AppLocaleOption[];
  t: (key: string, values?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

// Apply the stored locale before paint on the client. The server render has no
// localStorage, so the initializer stays English and this effect corrects it
// without a hydration mismatch.
const useClientLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Locale for chrome that renders outside `LocaleProvider`. The first render
 * stays English on both server and client; the stored preference is applied
 * in a layout effect, after hydration, so a saved language does not mismatch
 * the server HTML.
 */
export function useStoredLocale(): AppLocale {
  const [locale, setLocale] = useState<AppLocale>(DEFAULT_APP_LOCALE);
  useClientLayoutEffect(() => {
    setLocale(readStoredLocale());
  }, []);
  return locale;
}

export function LocaleProvider({
  children,
  catalogs,
  htmlRootSelector,
  syncHtmlLang = true,
}: {
  children: ReactNode;
  catalogs: LocaleCatalogs;
  htmlRootSelector?: string;
  syncHtmlLang?: boolean;
}) {
  const settings = useOptionalEnabledLocales();
  const enabledLocales = settings?.enabledLocales ?? APP_LOCALES;
  const settingsLoading = settings?.isLoading ?? false;
  const settingsLoadFailed = settings?.loadFailed ?? false;
  const [locale, setLocaleState] = useState<AppLocale>(DEFAULT_APP_LOCALE);
  const restoredStoredLocale = useRef(false);
  // A failed fetch fail-closes to English-only, which is indistinguishable from
  // an admin disabling every other language. Only act on a list the service
  // actually returned, so a transient 404/5xx cannot overwrite the stored
  // preference. While the first fetch is in flight the provider's list is still
  // the English placeholder — keep the stored locale on screen so dismissing
  // the loading overlay does not flash English, including document lang.
  // When no settings provider is mounted (login / loading chrome), every known
  // locale stays enabled.
  const settingsPending = Boolean(settings) && settingsLoading && !settingsLoadFailed;
  const settingsResolved = !settings || (!settingsLoading && !settingsLoadFailed);
  const policyLocales = settingsPending ? APP_LOCALES : enabledLocales;
  // Settings can change between render and the reconciliation effect below.
  // Derive the public value synchronously so Select never receives a disabled
  // value and translated content never gets one extra frame in that locale
  // once the tenant list is known.
  const activeLocale = policyLocales.includes(locale) ? locale : DEFAULT_APP_LOCALE;

  useClientLayoutEffect(() => {
    if (restoredStoredLocale.current) return;
    restoredStoredLocale.current = true;
    setLocaleState(readStoredLocale());
  }, []);

  useEffect(() => {
    if (!settingsResolved || !restoredStoredLocale.current || enabledLocales.includes(locale)) return;
    setLocaleState(DEFAULT_APP_LOCALE);
    storeAppLocale(DEFAULT_APP_LOCALE);
  }, [enabledLocales, locale, settingsResolved]);

  // Set this during render so children that call dayjs().fromNow() in the same
  // pass (favorites, notes, captures) see the new language. An effect would
  // run after that paint and the relative times would stay in the old language.
  dayjs.locale(activeLocale);

  useClientLayoutEffect(() => {
    if (!syncHtmlLang) return;

    const root = htmlRootSelector ? document.querySelector(htmlRootSelector) : null;
    const previousRootLang = root instanceof HTMLElement ? root.getAttribute("lang") : null;
    const previousDocumentLang = document.documentElement.getAttribute("lang");
    const htmlLang = localeHtmlLang(activeLocale);

    if (root instanceof HTMLElement) {
      root.lang = htmlLang;
    }
    document.documentElement.lang = htmlLang;

    return () => {
      if (root instanceof HTMLElement) {
        if (previousRootLang == null) root.removeAttribute("lang");
        else root.setAttribute("lang", previousRootLang);
      }
      if (previousDocumentLang == null) {
        document.documentElement.removeAttribute("lang");
      } else {
        document.documentElement.setAttribute("lang", previousDocumentLang);
      }
    };
  }, [activeLocale, htmlRootSelector, syncHtmlLang]);

  const setLocale = useCallback(
    (next: AppLocale) => {
      if (!policyLocales.includes(next)) return;
      setLocaleState(next);
      storeAppLocale(next);
    },
    [policyLocales],
  );

  const enabledLocaleOptions = useMemo(
    () => LOCALE_OPTIONS.filter((option) => policyLocales.includes(option.code)),
    [policyLocales],
  );

  const t = useCallback(
    (key: string, values?: Record<string, string | number>) =>
      translateFromCatalogs(catalogs, activeLocale, key, values),
    [activeLocale, catalogs],
  );

  const value = useMemo(
    () => ({ locale: activeLocale, setLocale, enabledLocaleOptions, t }),
    [activeLocale, setLocale, enabledLocaleOptions, t],
  );

  return (
    <I18nContext.Provider value={value}>
      <ThemeLocaleProvider locale={activeLocale}>{children}</ThemeLocaleProvider>
    </I18nContext.Provider>
  );
}

export function useOptionalI18n(): I18nValue | null {
  return useContext(I18nContext);
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within LocaleProvider");
  }
  return ctx;
}

export function useT() {
  return useI18n().t;
}

/**
 * Copy the provider locale to a parent that renders this provider.
 * ``Main`` translates toasts before the provider in the tree, so it cannot
 * read the context itself. The first call is still English, matching the
 * provider's own first paint.
 */
export function SyncLocale({ onLocale }: { onLocale: (locale: AppLocale) => void }) {
  const { locale } = useI18n();
  useClientLayoutEffect(() => {
    onLocale(locale);
  }, [locale, onLocale]);
  return null;
}
